import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { ScreenHeader, Surface, IconButton, Button, Badge, Avatar, Divider, Icon, BottomSheet, FormField, inputCss, FeedbackMsg, AlunoSearch } from '../components/ui';

const API_URL = process.env.REACT_APP_API_URL;

const WEEK_SHORT  = ['D','S','T','Q','Q','S','S'];
const WEEK_FULL   = ['Dom','Seg','Ter','Qua','Qui','Sex','Sáb'];
const MONTH_NAMES = ['Janeiro','Fevereiro','Março','Abril','Maio','Junho','Julho','Agosto','Setembro','Outubro','Novembro','Dezembro'];

// ── Fuso Recife ───────────────────────────────────────────────
function nowRecife() {
  return new Date(new Date().toLocaleString('en-US', { timeZone: 'America/Recife' }));
}
function toDateStr(year, month, day) {
  return `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}
function dateObjToStr(d) {
  return toDateStr(d.getFullYear(), d.getMonth(), d.getDate());
}

// ── Cores por aluno ───────────────────────────────────────────
// Cor gerada proceduralmente a partir do nome (não há paleta fixa, são
// centenas de variações). Determinística: o MESMO aluno tem SEMPRE a mesma
// cor, em todas as visões. Alunos diferentes recebem matizes diferentes ao
// longo dos 360° do círculo cromático, com variação de tom/saturação para
// separar nomes que cairiam num matiz parecido.
function hashCode(str) {
  let h = 0;
  for (let i = 0; i < str.length; i++) h = (str.charCodeAt(i) + ((h << 5) - h)) | 0;
  return Math.abs(h);
}
function hslToHex(h, s, l) {
  l /= 100;
  const a = s * Math.min(l, 1 - l) / 100;
  const f = n => {
    const k = (n + h / 30) % 12;
    const c = l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
    return Math.round(255 * c).toString(16).padStart(2, '0');
  };
  return `#${f(0)}${f(8)}${f(4)}`;
}

// 3 tons (claro/médio/vívido) — multiplica as variações e separa alunos
// mesmo quando há muitos (vizinhos no matiz caem em tons diferentes).
function makeColor(hue, tier) {
  const sat = [72, 86, 60][tier];
  return {
    bg:   hslToHex(hue, sat, [90, 85, 93][tier]),               // fundo claro
    text: hslToHex(hue, Math.min(sat + 12, 96), [30, 26, 35][tier]), // texto/linha escuros do mesmo matiz
  };
}

// Mapa global aluno → cor. Os matizes são distribuídos uniformemente pelos
// 360° entre todos os alunos que têm aula, garantindo a MAIOR separação
// possível (≈360°/N) — alunos diferentes nunca ficam com cor parecida.
// É determinístico (ordem alfabética), então cada aluno mantém SEMPRE a mesma
// cor em todas as visões. Como não há paleta fixa, o nº de variações é
// praticamente ilimitado (360 matizes × 3 tons).
let STUDENT_COLORS = {};
function buildStudentColors(names) {
  const unique = [...new Set(names.filter(Boolean))].sort((a, b) => a.localeCompare(b, 'pt'));
  const N = Math.max(unique.length, 1);
  const map = {};
  unique.forEach((n, i) => {
    // +180° p/ índices ímpares: vizinhos alfabéticos caem em lados opostos do
    // círculo; o tom alterna a cada aluno p/ reforçar a distinção
    const hue  = Math.round((i * 360 / N + (i % 2) * 180) % 360);
    const tier = i % 3;
    map[n] = makeColor(hue, tier);
  });
  STUDENT_COLORS = map;
}

// Fallback p/ nome fora do mapa (ex.: aluno sem aula carregada): cor por hash
function fallbackColor(key) {
  const h = hashCode(key);
  return makeColor(h % 360, Math.floor(h / 360) % 3);
}
function studentColor(name) {
  const key = name || '';
  return STUDENT_COLORS[key] || fallbackColor(key);
}
// Cores de uma lista de eventos — cada um pela cor fixa do seu aluno
function eventColors(events) {
  return events.map(ev => studentColor(ev.aluno || ''));
}

// ── Helpers de calendário ─────────────────────────────────────
function buildCalendar(year, month) {
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  return cells;
}
function weekOf(year, month, day) {
  const base = new Date(year, month, day);
  const dow  = base.getDay();
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(base);
    d.setDate(base.getDate() - dow + i);
    return d;
  });
}

// ── Helpers de tempo ─────────────────────────────────────────
const PX_PER_MIN  = 0.9;   // pixels por minuto
const GRID_START  = 7 * 60; // 07:00
const GRID_END    = 22 * 60; // 22:00
const GRID_HEIGHT = (GRID_END - GRID_START) * PX_PER_MIN;
const HOUR_H      = 60 * PX_PER_MIN; // altura de 1 hora em px

function timeToMin(hm) {
  const [h, m] = hm.split(':').map(Number);
  return h * 60 + m;
}
function minToHHMM(min) {
  return `${String(Math.floor(min / 60)).padStart(2, '0')}:${String(min % 60).padStart(2, '0')}`;
}
function durLabel(min) {
  const h = Math.floor(min / 60), m = min % 60;
  return h > 0 ? (m > 0 ? `${h}h${m}min` : `${h}h`) : `${m}min`;
}

// ── Pílula de evento (visão mês/semana) ───────────────────────
function Pill({ c, col, onClick }) {
  if (!col) col = studentColor(c.aluno || '');
  const nome = (c.aluno || 'Aula').split(' ')[0].slice(0, 6);
  return (
    <button
      onClick={e => { e.stopPropagation(); onClick(c); }}
      title={`${c.hora_inicio} · ${c.aluno}`}
      style={{
        display: 'block', width: '100%', textAlign: 'left',
        background: col.bg, color: col.text,
        border: 'none', borderRadius: 3,
        padding: '1px 3px', marginBottom: 1,
        fontSize: 9, fontWeight: 700, lineHeight: 1.5,
        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
        cursor: 'pointer',
      }}
    >
      {nome}
    </button>
  );
}

// ── Visão MÊS ─────────────────────────────────────────────────
function MonthView({ year, month, agendamentos, selDay, setSelDay, todayStr, onOpen }) {
  const cells  = buildCalendar(year, month);
  const byDate = {};
  agendamentos.forEach(a => { (byDate[a.data] = byDate[a.data] || []).push(a); });

  return (
    <>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 1, marginBottom: 2 }}>
        {WEEK_SHORT.map((l, i) => (
          <div key={i} style={{ textAlign: 'center', fontSize: 10, fontWeight: 600, color: 'var(--text-tertiary)', padding: '3px 0' }}>{l}</div>
        ))}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 1 }}>
        {cells.map((d, i) => {
          if (d === null) return <div key={i} style={{ minHeight: 62 }}/>;
          const dateStr  = toDateStr(year, month, d);
          const isToday  = dateStr === todayStr;
          const isSel    = d === selDay;
          const events   = (byDate[dateStr] || []).sort((a, b) => a.hora_inicio.localeCompare(b.hora_inicio));
          const colors   = eventColors(events);
          const shown    = events.slice(0, 2);
          const extra    = events.length - shown.length;
          return (
            <button key={i} onClick={() => setSelDay(d)} style={{
              minHeight: 62, padding: '2px', overflow: 'hidden',
              border: isSel ? '2px solid var(--accent)' : '1px solid var(--border-hairline)',
              borderRadius: 4, cursor: 'pointer', textAlign: 'left',
              background: isToday ? 'var(--accent-soft)' : 'var(--bg-surface)',
              display: 'flex', flexDirection: 'column',
            }}>
              <span style={{
                fontSize: 10, fontWeight: isToday ? 700 : 500, lineHeight: 1.8, paddingLeft: 2,
                color: isToday ? 'var(--accent)' : isSel ? 'var(--accent)' : 'var(--text-secondary)',
              }}>{d}</span>
              {shown.map((ev, j) => <Pill key={j} c={ev} col={colors[j]} onClick={onOpen}/>)}
              {extra > 0 && <span style={{ fontSize: 8, color: 'var(--text-tertiary)', paddingLeft: 2 }}>+{extra}</span>}
            </button>
          );
        })}
      </div>
    </>
  );
}

// ── Visão SEMANA — grid de tempo com posicionamento absoluto ──
function WeekView({ year, month, day, agendamentos, todayStr, nowHM, onOpen }) {
  const days   = weekOf(year, month, day);
  const byDate = {};
  agendamentos.forEach(a => { (byDate[a.data] = byDate[a.data] || []).push(a); });
  const byDateColors = {};
  days.forEach(d => {
    const ds = dateObjToStr(d);
    byDateColors[ds] = eventColors(byDate[ds] || []);
  });

  // Calcula range dinâmico baseado nas aulas da semana
  let rangeStart = GRID_END, rangeEnd = GRID_START;
  days.forEach(d => {
    const dateStr = dateObjToStr(d);
    (byDate[dateStr] || []).forEach(ev => {
      const s = timeToMin(ev.hora_inicio);
      const e = s + ev.duracao;
      if (s < rangeStart) rangeStart = s;
      if (e > rangeEnd)   rangeEnd   = e;
    });
  });
  // fallback se não houver aulas
  if (rangeStart >= rangeEnd) { rangeStart = 8 * 60; rangeEnd = 18 * 60; }
  // arredonda para hora cheia, com margem
  rangeStart = Math.max(GRID_START, Math.floor(rangeStart / 60) * 60 - 30);
  rangeEnd   = Math.min(GRID_END,   Math.ceil(rangeEnd   / 60) * 60 + 30);
  const totalH = (rangeEnd - rangeStart) * PX_PER_MIN;

  // Horas a exibir no eixo
  const hours = [];
  for (let m = rangeStart; m < rangeEnd; m += 60) hours.push(m);

  const TIME_W = 34;

  return (
    <div style={{ overflowX: 'auto' }}>
      {/* Cabeçalho dos dias */}
      <div style={{ display: 'flex', marginLeft: TIME_W }}>
        {days.map((d, i) => {
          const dateStr = dateObjToStr(d);
          const isToday = dateStr === todayStr;
          return (
            <div key={i} style={{ flex: 1, textAlign: 'center', paddingBottom: 6,
              borderBottom: isToday ? '2px solid var(--accent)' : '1px solid var(--border-hairline)' }}>
              <div style={{ fontSize: 9, color: 'var(--text-tertiary)', fontWeight: 600, textTransform: 'uppercase' }}>{WEEK_FULL[i]}</div>
              <div style={{ fontSize: 14, fontWeight: 700,
                color: isToday ? 'var(--accent)' : 'var(--text-primary)',
                fontFamily: '"Geist Mono", monospace' }}>{d.getDate()}</div>
            </div>
          );
        })}
      </div>

      {/* Grid */}
      <div style={{ display: 'flex', marginTop: 4 }}>
        {/* Eixo de horas */}
        <div style={{ width: TIME_W, flexShrink: 0, position: 'relative', height: totalH }}>
          {hours.map(m => (
            <div key={m} style={{
              position: 'absolute', top: (m - rangeStart) * PX_PER_MIN,
              width: '100%', textAlign: 'right', paddingRight: 6,
            }}>
              <span style={{ fontSize: 9, color: 'var(--text-tertiary)', fontFamily: '"Geist Mono", monospace',
                lineHeight: 1, transform: 'translateY(-50%)', display: 'inline-block' }}>
                {minToHHMM(m)}
              </span>
            </div>
          ))}
        </div>

        {/* Colunas dos dias */}
        {days.map((d, i) => {
          const dateStr = dateObjToStr(d);
          const isToday = dateStr === todayStr;
          const events  = (byDate[dateStr] || []);

          return (
            <div key={i} style={{ flex: 1, position: 'relative', height: totalH,
              borderLeft: '1px solid var(--border-hairline)',
              background: isToday ? 'color-mix(in oklch, var(--accent) 4%, transparent)' : 'transparent' }}>

              {/* Linhas de hora */}
              {hours.map(m => (
                <div key={m} style={{
                  position: 'absolute', top: (m - rangeStart) * PX_PER_MIN,
                  left: 0, right: 0, borderTop: '1px solid var(--border-hairline)',
                }}/>
              ))}

              {/* Linha "agora" */}
              {isToday && (() => {
                const nowMin = timeToMin(nowHM);
                if (nowMin >= rangeStart && nowMin <= rangeEnd) return (
                  <div style={{
                    position: 'absolute', top: (nowMin - rangeStart) * PX_PER_MIN,
                    left: 0, right: 0, borderTop: '2px solid var(--accent)', zIndex: 10,
                  }}>
                    <div style={{ width: 7, height: 7, borderRadius: 4, background: 'var(--accent)',
                      position: 'absolute', left: -4, top: -4 }}/>
                  </div>
                );
                return null;
              })()}

              {/* Eventos */}
              {events.map((ev, j) => {
                const col      = byDateColors[dateStr][j];
                const startMin = timeToMin(ev.hora_inicio);
                const top      = (startMin - rangeStart) * PX_PER_MIN;
                const height   = Math.max(ev.duracao * PX_PER_MIN, 20);
                const nome     = (ev.aluno || 'Aula').split(' ')[0];
                return (
                  <button key={j} onClick={() => onOpen(ev)} title={`${ev.hora_inicio} · ${ev.aluno}`}
                    style={{
                      position: 'absolute', top, left: 1, right: 1, height,
                      background: col.bg, color: col.text,
                      border: 'none', borderLeft: `3px solid ${col.text}`,
                      borderRadius: 3, padding: '2px 3px',
                      cursor: 'pointer', textAlign: 'left', overflow: 'hidden',
                      zIndex: 5,
                    }}>
                    <div style={{ fontSize: 9, fontWeight: 700, lineHeight: 1.3,
                      whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {nome}
                    </div>
                    {height >= 36 && (
                      <div style={{ fontSize: 8, opacity: 0.75, lineHeight: 1.3 }}>{ev.hora_inicio}</div>
                    )}
                  </button>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Visão DIA — grade de tempo com lacunas ────────────────────
function DayView({ dateStr, agendamentos, todayStr, nowHM, onOpen, tipo }) {
  const classes = agendamentos
    .filter(a => a.data === dateStr)
    .sort((a, b) => a.hora_inicio.localeCompare(b.hora_inicio));

  if (classes.length === 0) {
    return (
      <Surface padded style={{ textAlign: 'center', padding: 'var(--gap-4)' }}>
        <Icon name="calendar" size={28} style={{ margin: '0 auto 8px', display: 'block', opacity: 0.4, color: 'var(--text-tertiary)' }}/>
        <div style={{ fontSize: 13.5, color: 'var(--text-secondary)' }}>Nenhuma aula nesse dia.</div>
        {tipo === 'aluno' && (
          <Button size="sm" variant="secondary" icon="plus" style={{ marginTop: 12 }}
            onClick={() => window.location.href = '/horarios-disponiveis'}>
            Solicitar aula
          </Button>
        )}
      </Surface>
    );
  }

  // Pré-calcula cores sem repetição sequencial
  const classColors = eventColors(classes);

  // Monta lista: classes intercaladas com lacunas
  const items = [];
  classes.forEach((c, i) => {
    const startMin = timeToMin(c.hora_inicio);
    if (i > 0) {
      const prev = classes[i - 1];
      const prevEnd = timeToMin(prev.hora_inicio) + prev.duracao;
      const gap = startMin - prevEnd;
      if (gap > 0) items.push({ type: 'gap', duracao: gap, hora: minToHHMM(prevEnd) });
    }
    items.push({ type: 'aula', ...c, _i: i });
  });

  const TIME_W = 44; // largura da coluna de horário

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {items.map((item, i) => {
        const h = Math.max(item.duracao * PX_PER_MIN, 36);

        if (item.type === 'gap') {
          return (
            <div key={i} style={{ display: 'flex', alignItems: 'stretch', gap: 8, opacity: 0.55 }}>
              <div style={{ width: TIME_W, flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', paddingTop: 6 }}>
                <span style={{ fontFamily: '"Geist Mono", monospace', fontSize: 10, color: 'var(--text-tertiary)' }}>{item.hora}</span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: 10, flexShrink: 0 }}>
                <div style={{ width: 1, flex: 1, borderLeft: '1.5px dashed var(--border-default)' }}/>
              </div>
              <div style={{ flex: 1, height: h, display: 'flex', alignItems: 'center' }}>
                <span style={{ fontSize: 11, color: 'var(--text-tertiary)', fontStyle: 'italic' }}>
                  Livre · {durLabel(item.duracao)}
                </span>
              </div>
            </div>
          );
        }

        const col    = classColors[item._i];
        const isLive = dateStr === todayStr && item.hora_inicio <= nowHM;
        return (
          <div key={i} style={{ display: 'flex', alignItems: 'stretch', gap: 8 }}>
            {/* Hora */}
            <div style={{ width: TIME_W, flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', paddingTop: 8 }}>
              <span style={{ fontFamily: '"Geist Mono", monospace', fontSize: 11, fontWeight: 600, color: isLive ? 'var(--accent)' : 'var(--text-secondary)' }}>
                {item.hora_inicio}
              </span>
            </div>
            {/* Linha vertical */}
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: 10, flexShrink: 0 }}>
              <div style={{ width: 8, height: 8, borderRadius: 4, background: col.text, marginTop: 10, flexShrink: 0,
                boxShadow: isLive ? `0 0 0 3px ${col.bg}` : 'none' }}/>
              <div style={{ width: 1, flex: 1, background: col.text, opacity: 0.25 }}/>
            </div>
            {/* Bloco da aula */}
            <button onClick={() => onOpen(item)} style={{
              flex: 1, minHeight: h, textAlign: 'left', cursor: 'pointer',
              background: col.bg, border: 'none',
              borderLeft: `3px solid ${col.text}`,
              borderRadius: 'var(--radius-md)',
              padding: '8px 10px', marginBottom: 2,
              display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <Avatar name={item.aluno || 'A'} size={20}/>
                <span style={{ fontSize: 13, fontWeight: 700, color: col.text,
                  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {item.aluno}
                </span>
                {isLive && <Badge tone="accent">Ao vivo</Badge>}
                {item.status === 'pendente' && <Badge tone="warn">Pendente</Badge>}
              </div>
              {h >= 54 && (
                <div style={{ fontSize: 10.5, color: col.text, opacity: 0.75, display: 'flex', gap: 6, marginTop: 4 }}>
                  <Icon name={item.tipo === 'online' ? 'cam' : 'location'} size={10}/>
                  {item.tipo || 'Presencial'} · {durLabel(item.duracao)}
                </div>
              )}
            </button>
          </div>
        );
      })}
    </div>
  );
}

// ── Visão LISTA ───────────────────────────────────────────────
function ListView({ agendamentos, todayStr, onOpen }) {
  const upcoming = agendamentos
    .filter(a => a.status === 'aprovado' && a.data >= todayStr)
    .sort((a, b) => a.data.localeCompare(b.data) || a.hora_inicio.localeCompare(b.hora_inicio));

  if (upcoming.length === 0) {
    return (
      <Surface padded style={{ textAlign: 'center', padding: 'var(--gap-4)' }}>
        <Icon name="calendar" size={28} style={{ margin: '0 auto 8px', display: 'block', opacity: 0.4, color: 'var(--text-tertiary)' }}/>
        <div style={{ fontSize: 13.5, color: 'var(--text-secondary)' }}>Nenhuma aula próxima.</div>
      </Surface>
    );
  }

  const upcomingColors = eventColors(upcoming);
  let lastDate = null;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      {upcoming.map((c, i) => {
        const showHeader = c.data !== lastDate;
        lastDate = c.data;
        const [y, m, d] = c.data.split('-').map(Number);
        const isToday = c.data === todayStr;
        const dateLabel = isToday
          ? 'Hoje'
          : new Date(y, m - 1, d).toLocaleDateString('pt-BR', { weekday: 'short', day: 'numeric', month: 'short' });
        const col = upcomingColors[i];
        return (
          <React.Fragment key={i}>
            {showHeader && (
              <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: 0.5, marginTop: i > 0 ? 10 : 0 }}>
                {dateLabel}
              </div>
            )}
            <button onClick={() => onOpen(c)} style={{
              display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px',
              background: 'var(--bg-surface)', borderRadius: 'var(--radius-md)',
              border: '1px solid var(--border-hairline)',
              borderLeft: `4px solid ${col.text}`,
              cursor: 'pointer', textAlign: 'left', width: '100%',
            }}>
              <div style={{ minWidth: 42 }}>
                <div style={{ fontFamily: '"Geist Mono", monospace', fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>{c.hora_inicio}</div>
                <div style={{ fontSize: 10, color: 'var(--text-tertiary)' }}>{c.duracao}min</div>
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.aluno}</div>
                <div style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>{c.tipo || 'Presencial'}</div>
              </div>
              {c.status === 'pendente' && <Badge tone="warn">Pendente</Badge>}
            </button>
          </React.Fragment>
        );
      })}
    </div>
  );
}

// ── Componente principal ──────────────────────────────────────
export default function Aulas() {
  const { tipo } = useAuth();
  const [agendamentos, setAgendamentos] = useState([]);
  const [alunos, setAlunos]             = useState([]);
  const [loading, setLoading]           = useState(true);

  const today  = nowRecife();
  const [year,   setYear]   = useState(today.getFullYear());
  const [month,  setMonth]  = useState(today.getMonth());
  const [selDay, setSelDay] = useState(today.getDate());
  const [view,   setView]   = useState('mes'); // 'mes' | 'semana' | 'dia' | 'lista'

  const [detalhe,      setDetalhe]      = useState(null);
  const [editando,     setEditando]     = useState(false);
  const [editForm,     setEditForm]     = useState({});
  const [novaAulaOpen, setNovaAulaOpen] = useState(false);
  const [novaAula,     setNovaAula]     = useState({ aluno_id: '', data: '', hora_inicio: '', duracao: 60, tipo: 'presencial', modalidade: 'particular' });
  const [msg,    setMsg]    = useState('');
  const [saving, setSaving] = useState(false);

  const todayStr  = toDateStr(today.getFullYear(), today.getMonth(), today.getDate());
  const nowHM     = `${String(today.getHours()).padStart(2,'0')}:${String(today.getMinutes()).padStart(2,'0')}`;
  const selDateStr = toDateStr(year, month, selDay);

  // Recalcula o mapa global de cores por aluno sempre que a lista de aulas muda.
  // Roda no corpo do render (antes das views) p/ studentColor já ter o mapa pronto.
  useMemo(() => buildStudentColors(agendamentos.map(a => a.aluno)), [agendamentos]);

  function carregarAgendamentos() {
    return axios.get(`${API_URL}/agendamentos`, { withCredentials: true })
      .then(r => setAgendamentos(r.data || [])).catch(() => {});
  }

  useEffect(() => {
    carregarAgendamentos().finally(() => setLoading(false));
    if (tipo === 'professora') {
      axios.get(`${API_URL}/alunos_aprovados`, { withCredentials: true })
        .then(r => setAlunos(r.data || [])).catch(() => {});
    }
  }, [tipo]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Navegação ─────────────────────────────────────────────
  function goToday() {
    const n = nowRecife();
    setYear(n.getFullYear()); setMonth(n.getMonth()); setSelDay(n.getDate());
  }

  function goPrev() {
    if (view === 'dia') {
      const d = new Date(year, month, selDay - 1);
      setYear(d.getFullYear()); setMonth(d.getMonth()); setSelDay(d.getDate());
    } else if (view === 'semana') {
      const d = new Date(year, month, selDay - 7);
      setYear(d.getFullYear()); setMonth(d.getMonth()); setSelDay(d.getDate());
    } else {
      if (month === 0) { setYear(y => y - 1); setMonth(11); } else setMonth(m => m - 1);
      setSelDay(1);
    }
  }

  function goNext() {
    if (view === 'dia') {
      const d = new Date(year, month, selDay + 1);
      setYear(d.getFullYear()); setMonth(d.getMonth()); setSelDay(d.getDate());
    } else if (view === 'semana') {
      const d = new Date(year, month, selDay + 7);
      setYear(d.getFullYear()); setMonth(d.getMonth()); setSelDay(d.getDate());
    } else {
      if (month === 11) { setYear(y => y + 1); setMonth(0); } else setMonth(m => m + 1);
      setSelDay(1);
    }
  }

  // ── Label do período atual ────────────────────────────────
  function periodLabel() {
    if (view === 'dia') {
      return `${selDay} de ${MONTH_NAMES[month].toLowerCase()}`;
    }
    if (view === 'semana') {
      const days = weekOf(year, month, selDay);
      const first = days[0], last = days[6];
      if (first.getMonth() === last.getMonth())
        return `${first.getDate()}–${last.getDate()} ${MONTH_NAMES[first.getMonth()].toLowerCase()}`;
      return `${first.getDate()} ${MONTH_NAMES[first.getMonth()].toLowerCase().slice(0,3)} – ${last.getDate()} ${MONTH_NAMES[last.getMonth()].toLowerCase().slice(0,3)}`;
    }
    return `${MONTH_NAMES[month].toLowerCase()} ${year}`;
  }

  // ── Nova aula ─────────────────────────────────────────────
  function abrirNovaAula() {
    setNovaAula({ aluno_id: '', data: selDateStr, hora_inicio: '', duracao: 60, tipo: 'presencial', modalidade: 'particular' });
    setMsg(''); setNovaAulaOpen(true);
  }

  function salvarNovaAula(e) {
    e.preventDefault(); setSaving(true); setMsg('');
    axios.post(`${API_URL}/aula_professora`, novaAula, { withCredentials: true })
      .then(() => { setNovaAulaOpen(false); carregarAgendamentos(); })
      .catch(err => setMsg(err.response?.data?.erro || 'Erro ao agendar aula.'))
      .finally(() => setSaving(false));
  }

  function abrirEdicao(c) {
    setEditForm({ data: c.data, hora_inicio: c.hora_inicio, duracao: c.duracao, tipo: c.tipo || 'presencial', modalidade: c.modalidade || 'particular' });
    setMsg('');
    setEditando(true);
  }

  function salvarEdicao(e) {
    e.preventDefault();
    setSaving(true);
    setMsg('');
    axios.put(`${API_URL}/agendamentos/${detalhe.id}`, editForm, { withCredentials: true })
      .then(() => {
        setDetalhe(null);
        setEditando(false);
        carregarAgendamentos();
      })
      .catch(err => setMsg(err.response?.data?.erro || 'Erro ao alterar aula.'))
      .finally(() => setSaving(false));
  }

  function excluirAula(id) {
    if (!window.confirm('Deseja excluir esta aula?')) return;
    axios.delete(`${API_URL}/agendamentos/${id}`, { withCredentials: true })
      .then(() => { setDetalhe(null); carregarAgendamentos(); })
      .catch(err => alert(err.response?.data?.erro || 'Erro ao excluir.'));
  }

  function responderAula(id, resposta) {
    axios.post(`${API_URL}/agendamentos/${id}/responder`, { resposta }, { withCredentials: true })
      .then(() => { setDetalhe(null); carregarAgendamentos(); })
      .catch(err => alert(err.response?.data?.erro || 'Erro ao responder.'));
  }

  // ── Estilos dos botões de aba ─────────────────────────────
  const tabBtn = (active) => ({
    flex: 1, padding: '5px 0', fontSize: 12, fontWeight: 600, border: 'none', cursor: 'pointer',
    borderRadius: 'var(--radius-sm)', fontFamily: 'inherit',
    background: active ? 'var(--bg-surface)' : 'transparent',
    color: active ? 'var(--text-primary)' : 'var(--text-tertiary)',
    boxShadow: active ? 'var(--shadow-xs, 0 1px 3px rgba(0,0,0,.08))' : 'none',
    transition: 'all .15s',
  });

  return (
    <div className="mema-screen mema-scroll" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--gap-2)', paddingBottom: 90, overflow: 'auto' }}>
      <ScreenHeader title="Agenda" action={
        tipo === 'professora'
          ? <IconButton icon="plus" variant="accent" size={36} label="Nova aula" onClick={abrirNovaAula}/>
          : null
      }/>

      {/* ── Navegação + período ── */}
      <div style={{ padding: '0 var(--gap-3)', display: 'flex', alignItems: 'center', gap: 8 }}>
        <button onClick={goToday} style={{
          fontSize: 12, fontWeight: 600, padding: '5px 10px', border: '1px solid var(--border-default)',
          borderRadius: 'var(--radius-sm)', background: 'var(--bg-surface)', color: 'var(--text-secondary)',
          cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap',
        }}>Hoje</button>
        <IconButton icon="chevron" size={30} variant="surface" style={{ transform: 'rotate(180deg)' }} onClick={goPrev} label="Anterior"/>
        <IconButton icon="chevron" size={30} variant="surface" onClick={goNext} label="Próximo"/>
        <div style={{ flex: 1, fontSize: 15, fontWeight: 600, letterSpacing: -0.4, textAlign: 'right', textTransform: 'capitalize', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {periodLabel()}
        </div>
      </div>

      {/* ── Tabs de visão ── */}
      <div style={{ padding: '0 var(--gap-3)' }}>
        <div style={{ display: 'flex', gap: 2, background: 'var(--bg-sunken)', borderRadius: 'var(--radius-md)', padding: 3 }}>
          {[['mes','Mês'],['semana','Semana'],['dia','Dia'],['lista','Lista']].map(([v, label]) => (
            <button key={v} onClick={() => setView(v)} style={tabBtn(view === v)}>{label}</button>
          ))}
        </div>
      </div>

      {/* ── Conteúdo da visão ── */}
      <div style={{ padding: '0 var(--gap-3)' }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: 'var(--gap-4)', color: 'var(--text-tertiary)', fontSize: 13 }}>Carregando…</div>
        ) : view === 'mes' ? (
          <Surface padded>
            <MonthView year={year} month={month} agendamentos={agendamentos}
              selDay={selDay} setSelDay={d => { setSelDay(d); setView('dia'); }}
              todayStr={todayStr} tipo={tipo} onOpen={setDetalhe}/>
          </Surface>
        ) : view === 'semana' ? (
          <Surface padded>
            <WeekView year={year} month={month} day={selDay} agendamentos={agendamentos}
              todayStr={todayStr} nowHM={nowHM} onOpen={setDetalhe}/>
          </Surface>
        ) : view === 'dia' ? (
          <>
            <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 'var(--gap-2)' }}>
              <div style={{ fontSize: 11, fontWeight: 540, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                {selDateStr === todayStr ? 'Hoje' : 'Selecionado'}
              </div>
              <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>
                {agendamentos.filter(a => a.data === selDateStr).length} aula(s)
              </span>
            </div>
            <DayView dateStr={selDateStr} agendamentos={agendamentos}
              todayStr={todayStr} nowHM={nowHM} onOpen={setDetalhe} tipo={tipo}/>
          </>
        ) : (
          <ListView agendamentos={agendamentos} todayStr={todayStr} onOpen={setDetalhe}/>
        )}
      </div>

      {/* Hint aluno */}
      {tipo === 'aluno' && view !== 'lista' && (
        <div style={{ padding: '0 var(--gap-3)' }}>
          <button onClick={() => window.location.href = '/horarios-disponiveis'} style={{
            width: '100%', display: 'flex', alignItems: 'center', gap: 'var(--gap-2)',
            padding: 'var(--pad-card)', cursor: 'pointer',
            background: 'transparent', border: '1px dashed var(--border-default)',
            borderRadius: 'var(--radius-lg)', color: 'var(--text-secondary)',
            fontFamily: 'inherit', fontSize: 13,
          }}>
            <Icon name="clock" size={16}/>
            <span style={{ flex: 1, textAlign: 'left' }}>Ver horários disponíveis</span>
            <Icon name="chevron" size={13}/>
          </button>
        </div>
      )}

      {/* ── Detalhe da aula ── */}
      {detalhe && (
        <BottomSheet
          title={editando ? 'Alterar aula' : 'Detalhes da aula'}
          onClose={() => { setDetalhe(null); setEditando(false); }}
        >
          {editando ? (
            /* ── Formulário de edição ── */
            <form onSubmit={salvarEdicao} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--gap-3)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--gap-2)', marginBottom: 4 }}>
                <div style={{ width: 5, height: 36, borderRadius: 4, background: studentColor(detalhe.aluno || '').text, flexShrink: 0 }}/>
                <Avatar name={detalhe.aluno || 'A'} size={36}/>
                <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-primary)' }}>{detalhe.aluno}</div>
              </div>
              <FormField label="Data">
                <input type="date" style={inputCss} value={editForm.data}
                  onChange={e => setEditForm(f => ({ ...f, data: e.target.value }))} required/>
              </FormField>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--gap-2)' }}>
                <FormField label="Hora início">
                  <input type="time" style={inputCss} value={editForm.hora_inicio}
                    onChange={e => setEditForm(f => ({ ...f, hora_inicio: e.target.value }))} required/>
                </FormField>
                <FormField label="Duração (min)">
                  <input type="number" style={inputCss} min={1} value={editForm.duracao}
                    onChange={e => setEditForm(f => ({ ...f, duracao: Number(e.target.value) }))} required/>
                </FormField>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--gap-2)' }}>
                <FormField label="Tipo">
                  <select style={inputCss} value={editForm.tipo}
                    onChange={e => setEditForm(f => ({ ...f, tipo: e.target.value }))} required>
                    <option value="presencial">Presencial</option>
                    <option value="online">Online</option>
                  </select>
                </FormField>
                <FormField label="Modalidade">
                  <select style={inputCss} value={editForm.modalidade}
                    onChange={e => setEditForm(f => ({ ...f, modalidade: e.target.value }))} required>
                    <option value="particular">Particular</option>
                    <option value="grupo">Grupo</option>
                  </select>
                </FormField>
              </div>
              <FeedbackMsg msg={msg}/>
              <div style={{ display: 'flex', gap: 'var(--gap-2)' }}>
                <Button type="button" variant="secondary" style={{ flex: 1 }} onClick={() => setEditando(false)}>Cancelar</Button>
                <Button variant="primary" style={{ flex: 1 }} disabled={saving}>
                  {saving ? 'Salvando…' : 'Salvar'}
                </Button>
              </div>
            </form>
          ) : (
            /* ── Visualização ── */
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--gap-3)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--gap-2)' }}>
                <div style={{ width: 6, height: 40, borderRadius: 4, background: studentColor(detalhe.aluno || '').text, flexShrink: 0 }}/>
                <Avatar name={detalhe.aluno || 'A'} size={40}/>
                <div>
                  <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-primary)' }}>{detalhe.aluno || '—'}</div>
                  <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>Aluno</div>
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--gap-2)' }}>
                {[['Data', detalhe.data], ['Horário', detalhe.hora_inicio], ['Duração', `${detalhe.duracao} min`], ['Tipo', detalhe.tipo || 'Presencial'], ['Modalidade', detalhe.modalidade === 'grupo' ? 'Grupo' : 'Particular']].map(([label, value]) => (
                  <div key={label} style={{ background: 'var(--bg-sunken)', borderRadius: 'var(--radius-sm)', padding: '10px 12px' }}>
                    <div style={{ fontSize: 10.5, color: 'var(--text-tertiary)', fontWeight: 540, textTransform: 'uppercase', letterSpacing: 0.4 }}>{label}</div>
                    <div style={{ fontSize: 14, fontWeight: 600, marginTop: 2, color: 'var(--text-primary)' }}>{value}</div>
                  </div>
                ))}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>Status:</span>
                <Badge tone={detalhe.status === 'aprovado' ? 'success' : detalhe.status === 'rejeitado' ? 'danger' : 'warn'}>{detalhe.status}</Badge>
              </div>
              {tipo === 'professora' && detalhe.status === 'pendente' && (
                <div style={{ display: 'flex', gap: 'var(--gap-2)' }}>
                  <Button variant="primary" style={{ flex: 1 }} onClick={() => responderAula(detalhe.id, 'aprovado')}>Aprovar</Button>
                  <Button variant="secondary" style={{ flex: 1 }} onClick={() => responderAula(detalhe.id, 'rejeitado')}>Negar</Button>
                </div>
              )}
              {tipo === 'professora' && (
                <div style={{ display: 'flex', gap: 'var(--gap-2)' }}>
                  <Button variant="secondary" style={{ flex: 1 }} onClick={() => abrirEdicao(detalhe)}>Alterar</Button>
                  <Button variant="danger" style={{ flex: 1 }} onClick={() => excluirAula(detalhe.id)}>Excluir</Button>
                </div>
              )}
            </div>
          )}
        </BottomSheet>
      )}

      {/* ── Nova aula ── */}
      {novaAulaOpen && (
        <BottomSheet title="Nova aula" onClose={() => setNovaAulaOpen(false)}>
          <form onSubmit={salvarNovaAula} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--gap-3)' }}>
            <FormField label="Aluno">
              <AlunoSearch alunos={alunos} value={novaAula.aluno_id} onChange={id => setNovaAula(n => ({ ...n, aluno_id: id }))}/>
            </FormField>
            <FormField label="Data">
              <input type="date" style={inputCss} value={novaAula.data}
                onChange={e => setNovaAula(n => ({ ...n, data: e.target.value }))} required/>
            </FormField>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--gap-2)' }}>
              <FormField label="Hora início">
                <input type="time" style={inputCss} value={novaAula.hora_inicio}
                  onChange={e => setNovaAula(n => ({ ...n, hora_inicio: e.target.value }))} required/>
              </FormField>
              <FormField label="Duração (min)">
                <input type="number" style={inputCss} min={1} value={novaAula.duracao}
                  onChange={e => setNovaAula(n => ({ ...n, duracao: Number(e.target.value) }))} required/>
              </FormField>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--gap-2)' }}>
              <FormField label="Tipo">
                <select style={inputCss} value={novaAula.tipo}
                  onChange={e => setNovaAula(n => ({ ...n, tipo: e.target.value }))} required>
                  <option value="presencial">Presencial</option>
                  <option value="online">Online</option>
                </select>
              </FormField>
              <FormField label="Modalidade">
                <select style={inputCss} value={novaAula.modalidade}
                  onChange={e => setNovaAula(n => ({ ...n, modalidade: e.target.value }))} required>
                  <option value="particular">Particular</option>
                  <option value="grupo">Grupo</option>
                </select>
              </FormField>
            </div>
            <FeedbackMsg msg={msg}/>
            <Button variant="primary" size="lg" disabled={saving}>{saving ? 'Salvando…' : 'Agendar aula'}</Button>
          </form>
        </BottomSheet>
      )}
    </div>
  );
}
